import { Box, LinearProgress, Typography, styled, useTheme } from '@mui/material'
import PriorityHighIcon from '@mui/icons-material/PriorityHighRounded'
import { Bucket, UserProfile } from '../../../../../graphql/generated'

export type DisplayBucket = Bucket & { isPlaceholder?: boolean }

const BucketName = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}))

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  borderRadius: '4px',
  height: theme.spacing(1),
  marginBottom: theme.spacing(1),
  marginRight: '2px',
  flex: 1
}))

const ProgressLabel = styled(Typography)(() => ({
  textAlign: 'left'
}))

interface BucketProgressProps {
  bucket: DisplayBucket
  userProfile: UserProfile
  color: any
  bgColor: string
}

export default function BucketProgress({ bucket, userProfile, color, bgColor }: BucketProgressProps) {
  const mediaCount = bucket.counters?.totalMedia ?? 0
  const trashCount = bucket.counters?.totalMediaInTrash ?? 0
  const mediaLimit = userProfile?.plan?.bucket_media_limit ?? 50
  const showAlert = mediaCount >= mediaLimit

  const totalMedia = mediaCount + trashCount

  const theme = useTheme()
  const placeholderFg = theme.palette.customGrey['400']
  const placeholderBg = theme.palette.customGrey['50']

  const displayFgColor = bucket.isPlaceholder ? placeholderFg : color
  const displayBgColor = bucket.isPlaceholder ? placeholderBg : bgColor

  return (
    <Box>
      <BucketName variant="pb.headline.medium" color={bucket.isPlaceholder ? placeholderFg : 'textPrimary'}>
        {bucket.title}
      </BucketName>
      <Box display="flex" flexDirection="row" height="10px" justifyContent="space-between">
        <ProgressBar
          sx={{
            '.MuiLinearProgress-bar': { backgroundColor: `${displayFgColor} !important` },
            '&.MuiLinearProgress-root': { backgroundColor: `${displayBgColor} !important`, opacity: 1 }
          }}
          variant="determinate"
          value={Math.min((totalMedia / mediaLimit) * 100, 100)}
        />
        {showAlert && (
          <Box width="8px" height="8px" fontSize="8px" bgcolor="error.main" borderRadius="50%">
            <PriorityHighIcon fontSize="inherit" sx={{ color: 'white' }} />
          </Box>
        )}
      </Box>
      <ProgressLabel
        variant="pb.label.small"
        color={bucket.isPlaceholder ? placeholderFg : color}
      >{`${totalMedia} of ${mediaLimit} images`}</ProgressLabel>
    </Box>
  )
}
